import React, { ChangeEvent, useState } from 'react';
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Typography,
} from '@mui/material';
import { Component } from './Component/Component.component';
import { selectCofStore, selectLofStore, selectRiskStore } from '@Selectors';
import {
	deleteCategory,
	deleteCoFCategory,
	setCoFComponents,
	setCoFConfig,
	setLoFComponents,
	setLoFConfig,
} from '@Actions';
import { useDispatch } from 'react-redux';
import { InfoCard } from '@innovyze/stylovyze';
import {
	Category as CategoryType,
	CategoryProps,
	Component as ComponentType,
} from '@Types';
import { useStyle } from './Category.styles';
import { WarningDialog } from '@innovyze/stylovyze';
import Scales from '@Components/Utils/icons/Scales';
import { useGlobalization } from '@Translations/useGlobalization';
import { noConditionData } from '@Components/RiskDetails/RiskDetails.component';

export const Category = ({
	isLoading,
	forLoF,
	LoFCategoryIndex,
	isWeightingInvalid = false,
	canBeDiscarded = true,
	onCategoryWeightingChanged,
	onAddComponent,
	onEditComponent,
}: CategoryProps) => {
	const { t } = useGlobalization();
	const classes = useStyle();
	const dispatch = useDispatch();

	const [open, setOpen] = useState(false);

	const riskStore = selectRiskStore();
	const lofStore = forLoF ? selectLofStore() : null;
	const cofStore = !forLoF ? selectCofStore() : null;

	let category: CategoryType | undefined = undefined;
	let components: ComponentType[] | undefined = undefined;

	if (forLoF) {
		if (
			LoFCategoryIndex !== undefined &&
			lofStore != undefined &&
			lofStore.categories?.length > 0
		) {
			category = lofStore?.categories[LoFCategoryIndex];
			components = category?.components;
		}
	} else {
		if (
			LoFCategoryIndex !== undefined &&
			cofStore != undefined &&
			cofStore.categories?.length > 0
		) {
			category = cofStore?.categories[LoFCategoryIndex];
			components = category?.components;
		}
	}

	const handleUseExclusivelyChange = (
		event: ChangeEvent<HTMLInputElement>,
	) => {
		if (lofStore) {
			dispatch(
				setLoFConfig(
					lofStore.categories.map((cat, index) =>
						index === LoFCategoryIndex
							? {
									...cat,
									useThisOnly: event.target.checked,
									weighting: event.target.checked
										? 100
										: cat.weighting,
							  }
							: cat,
					),
				),
			);
		}
	};

	const handleUseHighestChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!forLoF) {
			if (cofStore) {
				dispatch(
					setCoFConfig(
						cofStore.categories.map((cat, index) =>
							index === LoFCategoryIndex
								? {
										...cat,
										useHighestScore: event.target.checked,
								  }
								: cat,
						),
					),
				);
			}
		} else {
			if (lofStore) {
				dispatch(
					setLoFConfig(
						lofStore.categories.map((cat, index) =>
							index === LoFCategoryIndex
								? {
										...cat,
										useHighestScore: event.target.checked,
								  }
								: cat,
						),
					),
				);
			}
		}
	};

	const onDeleteComponent = (title: string) => {
		if (category && components) {
			if (forLoF) {
				dispatch(
					setLoFComponents({
						categoryName: category.name,
						components: components.filter(
							component => component.name !== title,
						),
					}),
				);
			} else {
				dispatch(
					setCoFComponents({
						categoryName: category.name,
						components: components.filter(
							component => component.name !== title,
						),
					}),
				);
			}
		}
	};

	const isInvalid = (): boolean => {
		const value = components
			?.filter(comp => comp.select)
			?.map(comp => comp.weighting)
			?.reduce((pre, current) => pre + current, 0);

		return value !== undefined && value > 0 && value !== 100;
	};

	const allComponents = () => {
		return components?.map((comp, index) => (
			<Component
				key={comp.name}
				title={comp.name}
				value={comp.weighting}
				enabledChecked={comp.select}
				invalid={isInvalid()}
				disableSlider={category?.weighting === 0}
				checkBox={category?.useHighestScore}
				onSliderValueChanged={(value: number) => {
					if (lofStore && components && category) {
						const newCat = {
							...category,
							components: components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: value,
											select: value !== 0,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setLoFConfig(
								lofStore.categories.map(category =>
									category.name === newCat.name
										? newCat
										: category,
								),
							),
						);
					} else if (cofStore && category && components) {
						const newCat = {
							...category,
							components: components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: value,
											select: value !== 0,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setCoFConfig(
								cofStore.categories.map(cof =>
									cof.name === newCat.name ? newCat : cof,
								),
							),
						);
					}
				}}
				onSelectChecked={(checked: boolean) => {
					if (lofStore && category) {
						const newCat = {
							...category,
							components: category.components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: 0,
											select: checked,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setLoFConfig(
								lofStore.categories.map(category =>
									category.name === newCat.name
										? newCat
										: category,
								),
							),
						);
					} else if (cofStore && components && category) {
						const newCat = {
							...category,
							components: category.components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: 0,
											select: checked,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setCoFConfig(
								cofStore.categories.map(cat =>
									cat.name === newCat.name ? newCat : cat,
								),
							),
						);
					}
				}}
				onEdit={title => {
					if (components) {
						const targetComponent = components.find(
							o => o.name == title,
						);
						if (targetComponent && onEditComponent) {
							onEditComponent(forLoF, category, {
								...targetComponent,
							});
						}
					}
				}}
				onDelete={onDeleteComponent}
			/>
		));
	};

	const ifLoadingTrue = (
		<Grid
			container
			item
			xs={12}
			justifyContent="center"
			style={{ paddingTop: '20px', paddingBottom: '20px' }}>
			<CircularProgress />
		</Grid>
	);
	const ifLoadingFalse =
		components && components.length > 0 ? (
			allComponents()
		) : (
			<Grid container>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{ paddingTop: '40px' }}>
					<Scales />
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography variant="h6">
						{t('You do not have any components added')}
					</Typography>
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography variant="caption">
						{t('Add components to set up weighting.')}
					</Typography>
				</Grid>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{
						paddingTop: '20px',
						paddingBottom: '20px',
					}}>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => onAddComponent?.(forLoF, category)}>
						{t('Add Component')}
					</Button>
				</Grid>
			</Grid>
		);

	const getCatWeightingDesc = () => {
		if (
			forLoF &&
			!noConditionData(riskStore.riskConfig.riskSetting.assetType)
		) {
			return category?.isCondition
				? t('Uncheck box above to adjust category weighting')
				: t('When there is no condition data');
		} else {
			return '';
		}
	};

	return (
		<div>
			<WarningDialog
				open={open}
				title={t('Discard category?')}
				content={t('Are you sure you want to discard this category?')}
				cancelText={t('Cancel')}
				confirmText={t('Confirm')}
				onCancel={() => setOpen(false)}
				onConfirm={() =>
					dispatch(
						forLoF
							? deleteCategory({
									forLoF: forLoF,
									categoryName: category?.name ?? '',
							  })
							: deleteCoFCategory({
									forLoF: forLoF,
									categoryName: category?.name ?? '',
							  }),
					)
				}
			/>
			<InfoCard
				title=""
				className={isLoading ? 'wait' : ''}
				titleArea={
					<Grid container className={isLoading ? 'wait' : ''}>
						<div className={classes.vl}></div>
						<Grid xs={6} item container>
							{category?.isCondition ? (
								<>
									<Grid item xs={12}>
										<Typography
											variant="h6"
											style={{ marginTop: '-5px' }}>
											{category.name}
										</Typography>
									</Grid>
									<Grid
										item
										xs={12}
										style={{ display: 'flex' }}>
										<FormControlLabel
											style={{
												marginLeft: '0px',
												marginTop: '-7px',
											}}
											control={
												<Checkbox
													style={{ padding: '0px' }}
													sx={{
														'& .MuiSvgIcon-root': {
															fontSize: 14,
														},
													}}
													checked={
														category?.useThisOnly
													}
													onChange={
														handleUseExclusivelyChange
													}
													inputProps={{
														'aria-label':
															'secondary checkbox',
													}}
												/>
											}
											label={
												<div
													title={t(
														'When selected you are choosing to override all general LoF components when condition is available for the asset.',
													)}>
													<Typography
														variant="body2"
														style={{
															marginLeft: '6px',
														}}>
														{t(`If condition data is
														present, use it
														exclusively for LoF`)}
													</Typography>
												</div>
											}
										/>
									</Grid>
								</>
							) : (
								<Grid item xs={12}>
									<Typography variant="h6">
										{category
											? category.name
											: forLoF
											? t('Category')
											: t('Components')}
									</Typography>
								</Grid>
							)}
						</Grid>
						<Grid
							xs={6}
							item
							container
							justifyContent="flex-end"
							style={{ paddingTop: '2px' }}>
							{!canBeDiscarded ||
							isLoading ||
							category?.isCondition ? null : (
								<Button
									variant="outlined"
									color="primary"
									onClick={() => setOpen(true)}
									style={{
										marginRight: '8px',
									}}>
									{t('Discard')}
								</Button>
							)}
							{isLoading ? null : (
								<Button
									variant="outlined"
									color="primary"
									onClick={() =>
										onAddComponent?.(forLoF, category)
									}>
									{t('Add Component')}
								</Button>
							)}
						</Grid>
					</Grid>
				}>
				{!isLoading ? (
					<Component
						title={t('Category Weighting')}
						invalid={
							(!category?.isCondition && isWeightingInvalid) ||
							(category?.isCondition &&
								!category.useThisOnly &&
								isWeightingInvalid)
						}
						subTitle={getCatWeightingDesc()}
						value={category ? category.weighting : 0}
						onSliderValueChanged={onCategoryWeightingChanged}
						needMenu={false}
						disableSlider={
							category?.isCondition && category.useThisOnly
						}
						enabledChecked={true}
					/>
				) : (
					<></>
				)}
				<Grid
					container
					className={classes.columnHeaderBreaker}
					style={{ display: 'flex' }}
					alignItems="center">
					<Grid xs={2} item>
						{t('Name')}
					</Grid>
					<Grid xs={4} item>
						{t(category?.useHighestScore ? 'Enabled' : 'Weight')}
					</Grid>
					{!isLoading ? (
						<Grid
							xs={6}
							item
							container
							style={{ justifyContent: 'flex-end' }}>
							<FormControlLabel
								control={
									<Checkbox
										style={{ padding: '0px' }}
										sx={{
											'& .MuiSvgIcon-root': {
												fontSize: 14,
											},
										}}
										disabled={category?.weighting === 0}
										checked={category?.useHighestScore}
										onChange={handleUseHighestChange}
									/>
								}
								label={
									<Typography
										variant="body2"
										style={{
											marginRight: '6px',
										}}>
										{t(`Use highest score from components as
										overall category score`)}
									</Typography>
								}
								labelPlacement="start"
							/>
						</Grid>
					) : (
						<Grid xs={6} item />
					)}
				</Grid>

				{isLoading ? ifLoadingTrue : ifLoadingFalse}
				<hr className={classes.divider} />
			</InfoCard>
		</div>
	);
};
